// The shared vocabulary is imported first, deliberately, and this line must stay
// above the App import. Vite emits CSS in module-graph order, so importing App
// first would bundle every screen's stylesheet ahead of styles.css — and a screen
// rule written at the same specificity as a `.card` or a `.tab` would then lose to
// it, silently, with nothing in the file to explain why.
import './styles.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.tsx';
import { Landing } from './Landing.tsx';
import { documentFor } from './legal/documents.ts';
import { LegalPage } from './legal/LegalPage.tsx';
import { Platform } from './platform/Platform.tsx';
import { SignIn } from './SignIn.tsx';
import { initRouting, useRoute } from './route.ts';
import { initTheme } from './theme.ts';

// Before createRoot, not inside an effect: an effect runs after the first paint,
// which is one frame of the wrong palette for anyone who has chosen dark. The
// inline script in index.html gets there earlier still; this reconciles with it.
initTheme();

// Same reasoning, for the same reason: this sets `data-surface` before the first
// paint, which is what releases the console's 1280px document floor on every
// public page — the landing page, sign-in and the three legal documents. Doing it in a component effect would paint a landing page inside
// a desktop viewport for one frame on every phone that loads it.
initRouting();

/**
 * Seven documents, one bundle.
 *
 * Deliberately a `switch` over the route and not a router: `route.ts` argues
 * why at length. Each branch is a whole page rather than a panel inside a
 * shared frame, because they genuinely share nothing — different audience,
 * different chrome, different width rules.
 *
 * The three legal documents are the exception that proves it: they share
 * everything, so they share a component and differ only in the data handed to
 * it. Three addresses, one renderer — which is the opposite trade from the four
 * above and made for the same reason, that the shape of the page follows from
 * who is reading it.
 */
const Root = () => {
  const route = useRoute();
  if (route.name === 'landing') return <Landing />;
  if (route.name === 'signin') return <SignIn />;
  if (route.name === 'terms') return <LegalPage doc={documentFor('terms')} />;
  if (route.name === 'privacy') return <LegalPage doc={documentFor('privacy')} />;
  if (route.name === 'dpa') return <LegalPage doc={documentFor('dpa')} />;
  if (route.name === 'admin') return <Platform />;
  return <App />;
};

const container = document.getElementById('root');
if (!container) throw new Error('No #root element in index.html');

createRoot(container).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
